import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { creditsService, sitesService, suppliersService } from '../services';
import { useSettings } from '../contexts/SettingsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import {
  Plus, Search, Loader2, Trash2, Eye, FileText,
  Calendar, ChevronLeft, ChevronRight, RotateCcw, ArrowLeft
} from 'lucide-react';
import { getApiError } from '@/lib/utils';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'posted', label: 'Posted' },
];

const getMonthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const getMonthEnd = () => {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(last.getDate()).padStart(2, '0')}`;
};

export default function CreditsRegisterPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { currencySymbol } = useSettings();
  const [credits, setCredits] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [siteFilter, setSiteFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState(getMonthStart());
  const [dateTo, setDateTo] = useState(getMonthEnd());
  const [page, setPage] = useState(0);
  const [sites, setSites] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const limit = 25;

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [sitesData, suppliersData] = await Promise.all([
          sitesService.list({ limit: 500 }),
          suppliersService.list({ limit: 500 }),
        ]);
        setSites(Array.isArray(sitesData) ? sitesData : sitesData.sites || sitesData.items || []);
        setSuppliers(suppliersData.items || []);
      } catch { /* silent */ }
    };
    loadFilters();
  }, []);

  const fetchCredits = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, offset: page * limit };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (siteFilter !== 'all') params.site_id = siteFilter;
      if (supplierFilter !== 'all') params.supplier_id = supplierFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const data = await creditsService.list(params);
      setCredits(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load credits', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, siteFilter, supplierFilter, dateFrom, dateTo, page, toast]);

  useEffect(() => {
    const timer = setTimeout(fetchCredits, 300);
    return () => clearTimeout(timer);
  }, [fetchCredits]);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this draft credit?')) return;
    setDeleting(id);
    try {
      await creditsService.delete(id);
      toast({ title: 'Deleted', description: 'Credit removed' });
      fetchCredits();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to delete'), variant: 'destructive' });
    } finally {
      setDeleting(null);
    }
  };

  const totalPages = Math.ceil(total / limit);

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '-';
    return `${currencySymbol}${Number(val).toFixed(2)}`;
  };

  return (
    <div className="space-y-6" data-testid="credits-register-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/stocktaking/dashboard')}
            data-testid="back-to-dashboard">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <RotateCcw className="h-6 w-6 text-slate-600" />
              Credit Register
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              View and manage all credit notes across sites
            </p>
          </div>
        </div>
        <Button onClick={() => navigate('/stocktaking/credits/new')} data-testid="new-credit-btn">
          <Plus className="h-4 w-4 mr-2" /> New Credit
        </Button>
      </div>

      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by reference..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                className="pl-9"
                data-testid="credit-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[140px]" data-testid="credit-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={siteFilter} onValueChange={(v) => { setSiteFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[180px]" data-testid="credit-site-filter">
                <SelectValue placeholder="All Sites" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sites</SelectItem>
                {sites.map(s => (
                  <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={supplierFilter} onValueChange={(v) => { setSupplierFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[180px]" data-testid="credit-supplier-filter">
                <SelectValue placeholder="All Suppliers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Suppliers</SelectItem>
                {suppliers.map(s => (
                  <SelectItem key={s.id} value={String(s.id)}>{s.supplier_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <Input type="date" value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                className="w-[140px] h-9 text-sm"
                data-testid="credit-date-from-filter" />
              <span className="text-sm text-muted-foreground">to</span>
              <Input type="date" value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                className="w-[140px] h-9 text-sm"
                data-testid="credit-date-to-filter" />
            </div>
            <Badge variant="outline" className="whitespace-nowrap ml-auto">
              {total} credit{total !== 1 ? 's' : ''}
            </Badge>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : credits.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No credits found</p>
              <Button variant="outline" size="sm" className="mt-3"
                onClick={() => navigate('/stocktaking/credits/new')}>
                <Plus className="h-4 w-4 mr-1" /> Create your first credit
              </Button>
            </div>
          ) : (
            <>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Site</TableHead>
                      <TableHead className="hidden md:table-cell">Supplier</TableHead>
                      <TableHead className="text-center">Lines</TableHead>
                      <TableHead className="text-right">Lines Total</TableHead>
                      <TableHead className="text-right hidden sm:table-cell">Credit Total</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {credits.map((c) => (
                      <TableRow
                        key={c.id}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => navigate(`/stocktaking/credits/${c.id}`)}
                        data-testid={`credit-row-${c.id}`}
                      >
                        <TableCell className="whitespace-nowrap text-sm">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                            {c.credit_date || '-'}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium text-sm">
                          {c.reference_number || <span className="text-muted-foreground italic">No ref</span>}
                        </TableCell>
                        <TableCell className="text-sm">{c.site_name || '-'}</TableCell>
                        <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                          {c.supplier_name || '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline" className="text-xs">{c.line_count}</Badge>
                        </TableCell>
                        <TableCell className="text-right text-sm font-mono">
                          {formatCurrency(c.lines_total)}
                        </TableCell>
                        <TableCell className="text-right text-sm font-mono hidden sm:table-cell">
                          {formatCurrency(c.credit_total)}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge
                            variant={c.status === 'posted' ? 'default' : 'secondary'}
                            className={`text-xs ${c.status === 'posted' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : ''}`}
                          >
                            {c.status === 'posted' ? 'Posted' : 'Draft'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8"
                              onClick={(e) => { e.stopPropagation(); navigate(`/stocktaking/credits/${c.id}`); }}
                              data-testid={`view-credit-${c.id}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            {c.status === 'draft' && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                                onClick={(e) => handleDelete(c.id, e)}
                                disabled={deleting === c.id}
                                data-testid={`delete-credit-${c.id}`}>
                                {deleting === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={page === 0}
                      onClick={() => setPage(p => p - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">Page {page + 1} of {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={page >= totalPages - 1}
                      onClick={() => setPage(p => p + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
